/**
 * Bookd - Venue Listing Page (Airbnb-style)
 * Multi-step form for listing a new venue
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { venueApi } from '../lib/api';
import { toast } from 'sonner';
import {
  Building2, MapPin, Users, DollarSign, Clock, Calendar,
  ChevronLeft, ChevronRight, Upload, X, Check, Info,
  Utensils, Wine, Music, Accessibility, Camera, Car,
  Wifi, Armchair, ChefHat, Bath, Loader2, Sparkles
} from 'lucide-react';

const VENUE_TYPES = [
  { value: 'event_space', label: 'Event Space', desc: 'Ballrooms, banquet halls, conference rooms' },
  { value: 'studio', label: 'Studio', desc: 'Photography, recording, dance studios' },
  { value: 'restaurant', label: 'Restaurant/Bar', desc: 'Dining venues, bars, lounges' },
  { value: 'outdoor', label: 'Outdoor Space', desc: 'Gardens, patios, rooftops' },
  { value: 'theater', label: 'Theater/Auditorium', desc: 'Performance venues, cinemas' },
  { value: 'gallery', label: 'Gallery', desc: 'Art galleries, exhibition spaces' },
  { value: 'warehouse', label: 'Warehouse/Loft', desc: 'Industrial spaces, lofts' },
  { value: 'rooftop', label: 'Rooftop', desc: 'Rooftop venues with views' },
  { value: 'private_home', label: 'Private Home', desc: 'Residential spaces for events' },
  { value: 'other', label: 'Other', desc: 'Unique or specialty venues' },
];

const AMENITIES = [
  { id: 'wifi', label: 'WiFi', icon: Wifi },
  { id: 'parking', label: 'Parking', icon: Car },
  { id: 'kitchen', label: 'Kitchen', icon: ChefHat },
  { id: 'sound_system', label: 'Sound System', icon: Music },
  { id: 'lighting', label: 'Professional Lighting', icon: Sparkles },
  { id: 'stage', label: 'Stage', icon: Music },
  { id: 'tables_chairs', label: 'Tables & Chairs', icon: Armchair },
  { id: 'av_equipment', label: 'A/V Equipment', icon: Camera },
  { id: 'wheelchair', label: 'Wheelchair Accessible', icon: Accessibility },
  { id: 'bathrooms', label: 'Multiple Bathrooms', icon: Bath },
  { id: 'catering_allowed', label: 'Catering Allowed', icon: Utensils },
  { id: 'alcohol_allowed', label: 'Alcohol Allowed', icon: Wine },
];

const EVENT_TYPES = [
  'Weddings', 'Corporate Events', 'Birthday Parties', 'Baby Showers',
  'Concerts', 'Art Exhibitions', 'Networking Events', 'Workshops',
  'Photo Shoots', 'Film Productions', 'Private Dinners', 'Holiday Parties',
  'Fundraisers', 'Product Launches', 'Fashion Shows', 'Other'
];

const STEPS = [
  { id: 1, title: 'Basics', desc: 'Venue type and name' },
  { id: 2, title: 'Location', desc: 'Address and access' },
  { id: 3, title: 'Details', desc: 'Capacity and features' },
  { id: 4, title: 'Amenities', desc: 'What you offer' },
  { id: 5, title: 'Rules', desc: 'Policies and restrictions' },
  { id: 6, title: 'Pricing', desc: 'Rates and fees' },
  { id: 7, title: 'Photos', desc: 'Showcase your space' },
  { id: 8, title: 'Review', desc: 'Confirm and publish' },
];

const ListVenue = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [photoUrls, setPhotoUrls] = useState([]);
  
  const [formData, setFormData] = useState({
    // Step 1: Basics
    name: '',
    venue_type: '',
    ownership_type: 'private', // private, commercial
    description: '',
    
    // Step 2: Location
    address: '',
    city: '',
    state: '',
    zip_code: '',
    freight_entrance: false,
    loading_dock: false,
    
    // Step 3: Details
    capacity: '',
    square_feet: '',
    num_bathrooms: 1,
    booking_type: 'entire', // entire, section
    
    // Step 4: Amenities
    amenities: [],
    kitchen_included: false,
    staff_available: false,
    staff_description: '',
    
    // Step 5: Rules & Policies
    event_types_allowed: [],
    minimum_age: 'all_ages', // all_ages, 18+, 21+
    smoking_allowed: false,
    hookah_allowed: false,
    has_cabaret_license: false,
    outside_catering_allowed: true,
    byob_allowed: false,
    has_security_cameras: false,
    check_in_time: '10:00',
    check_out_time: '22:00',
    house_rules: '',
    
    // Step 6: Pricing
    hourly_rate: '',
    daily_rate: '',
    minimum_hours: 2,
    cleaning_fee: '',
    security_deposit: '',
    offers_discount: false,
    discount_percent: '',
    discount_description: '',
    
    // Step 7: Photos
    photos: [],
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayItem = (field, item) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter(i => i !== item)
        : [...prev[field], item]
    }));
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // For now, create object URLs (in production, upload to cloud storage)
    const newUrls = files.map(file => URL.createObjectURL(file));
    setPhotoUrls(prev => [...prev, ...newUrls]);
    updateField('photos', [...formData.photos, ...newUrls]);
  };

  const removePhoto = (index) => {
    setPhotoUrls(prev => prev.filter((_, i) => i !== index));
    updateField('photos', formData.photos.filter((_, i) => i !== index));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.name && formData.venue_type;
      case 2:
        return formData.address && formData.city && formData.state;
      case 3:
        return formData.capacity;
      case 4:
        return true; // Amenities optional
      case 5:
        return formData.event_types_allowed.length > 0;
      case 6:
        return formData.hourly_rate || formData.daily_rate;
      case 7:
        return formData.photos.length > 0;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Add venue role to user if not already present
      if (!user.roles?.includes('venue')) {
        await updateUser({ roles: [...(user.roles || []), 'venue'] });
      }
      
      const venueData = {
        ...formData,
        capacity: parseInt(formData.capacity) || 0,
        square_feet: parseInt(formData.square_feet) || 0,
        hourly_rate: parseFloat(formData.hourly_rate) || 0,
        daily_rate: parseFloat(formData.daily_rate) || 0,
        cleaning_fee: parseFloat(formData.cleaning_fee) || 0,
        security_deposit: parseFloat(formData.security_deposit) || 0,
        discount_percent: parseFloat(formData.discount_percent) || 0,
      };
      
      await venueApi.create(venueData);
      toast.success('Venue listed successfully!');
      navigate('/venues');
    } catch (error) {
      console.error('Failed to create venue:', error);
      toast.error('Failed to list venue. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const progressPercent = (currentStep / STEPS.length) * 100;

  return (
    <div className="min-h-screen pt-20 pb-12 bg-gradient-to-b from-background to-muted/20">
      <div className="max-w-3xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
            List Your <span className="text-primary">Venue</span>
          </h1>
          <p className="text-muted-foreground">
            Share your space with event organizers and creators
          </p>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-muted-foreground">Step {currentStep} of {STEPS.length}</span>
            <span className="font-medium">{STEPS[currentStep - 1].title}</span>
          </div>
          <Progress value={progressPercent} className="h-2" />
          
          {/* Step indicators */}
          <div className="flex justify-between mt-4 overflow-x-auto pb-2">
            {STEPS.map((step) => (
              <div
                key={step.id}
                className={`flex flex-col items-center min-w-[60px] ${
                  step.id === currentStep
                    ? 'text-primary'
                    : step.id < currentStep
                    ? 'text-green-500'
                    : 'text-muted-foreground'
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium mb-1 ${
                    step.id === currentStep
                      ? 'bg-primary text-white'
                      : step.id < currentStep
                      ? 'bg-green-500 text-white'
                      : 'bg-muted'
                  }`}
                >
                  {step.id < currentStep ? <Check className="h-4 w-4" /> : step.id}
                </div>
                <span className="text-xs text-center hidden md:block">{step.title}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Form Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              {STEPS[currentStep - 1].title}
            </CardTitle>
            <CardDescription>{STEPS[currentStep - 1].desc}</CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Step 1: Basics */}
            {currentStep === 1 && (
              <>
                <div className="space-y-2">
                  <Label>Venue Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    placeholder="e.g., The Grand Ballroom"
                    className="rounded-xl"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Venue Type *</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {VENUE_TYPES.map((type) => (
                      <div
                        key={type.value}
                        onClick={() => updateField('venue_type', type.value)}
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          formData.venue_type === type.value
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <p className="font-medium text-sm">{type.label}</p>
                        <p className="text-xs text-muted-foreground">{type.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Ownership Type</Label>
                  <Select
                    value={formData.ownership_type}
                    onValueChange={(v) => updateField('ownership_type', v)}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="private">Private / Residential</SelectItem>
                      <SelectItem value="commercial">Commercial / Business</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => updateField('description', e.target.value)}
                    placeholder="Describe your venue, its ambiance, and what makes it special..."
                    className="rounded-xl min-h-[100px]"
                  />
                </div>
              </>
            )}

            {/* Step 2: Location */}
            {currentStep === 2 && (
              <>
                <div className="space-y-2">
                  <Label>Street Address *</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => updateField('address', e.target.value)}
                    placeholder="123 Main Street"
                    className="rounded-xl"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>City *</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => updateField('city', e.target.value)}
                      placeholder="New York"
                      className="rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State *</Label>
                    <Input
                      value={formData.state}
                      onChange={(e) => updateField('state', e.target.value)}
                      placeholder="NY"
                      className="rounded-xl"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>ZIP Code</Label>
                  <Input
                    value={formData.zip_code}
                    onChange={(e) => updateField('zip_code', e.target.value)}
                    placeholder="10001"
                    className="rounded-xl w-1/2"
                  />
                </div>
                
                <div className="space-y-4 pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Freight Entrance</p>
                      <p className="text-sm text-muted-foreground">Large entrance for equipment loading</p>
                    </div>
                    <Switch
                      checked={formData.freight_entrance}
                      onCheckedChange={(v) => updateField('freight_entrance', v)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Loading Dock</p>
                      <p className="text-sm text-muted-foreground">Dock for truck deliveries</p>
                    </div>
                    <Switch
                      checked={formData.loading_dock}
                      onCheckedChange={(v) => updateField('loading_dock', v)}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Step 3: Details */}
            {currentStep === 3 && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Maximum Capacity *</Label>
                    <Input
                      type="number"
                      value={formData.capacity}
                      onChange={(e) => updateField('capacity', e.target.value)}
                      placeholder="100"
                      className="rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Square Footage</Label>
                    <Input
                      type="number"
                      value={formData.square_feet}
                      onChange={(e) => updateField('square_feet', e.target.value)}
                      placeholder="2000"
                      className="rounded-xl"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Number of Bathrooms</Label>
                  <Select
                    value={formData.num_bathrooms.toString()}
                    onValueChange={(v) => updateField('num_bathrooms', parseInt(v))}
                  >
                    <SelectTrigger className="rounded-xl w-1/2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, '6+'].map((n) => (
                        <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Booking Type</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div
                      onClick={() => updateField('booking_type', 'entire')}
                      className={`p-4 rounded-xl border-2 cursor-pointer ${
                        formData.booking_type === 'entire'
                          ? 'border-primary bg-primary/5'
                          : 'border-border'
                      }`}
                    >
                      <p className="font-medium">Entire Space</p>
                      <p className="text-xs text-muted-foreground">Rent the whole venue</p>
                    </div>
                    <div
                      onClick={() => updateField('booking_type', 'section')}
                      className={`p-4 rounded-xl border-2 cursor-pointer ${
                        formData.booking_type === 'section'
                          ? 'border-primary bg-primary/5'
                          : 'border-border'
                      }`}
                    >
                      <p className="font-medium">Section/Area</p>
                      <p className="text-xs text-muted-foreground">Rent part of the venue</p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Step 4: Amenities */}
            {currentStep === 4 && (
              <>
                <div className="space-y-4">
                  <Label>Select all amenities available</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {AMENITIES.map((amenity) => (
                      <div
                        key={amenity.id}
                        onClick={() => toggleArrayItem('amenities', amenity.id)}
                        className={`p-3 rounded-xl border-2 cursor-pointer flex items-center gap-2 transition-all ${
                          formData.amenities.includes(amenity.id)
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <amenity.icon className="h-4 w-4 text-primary" />
                        <span className="text-sm">{amenity.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-4 pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Kitchen Included</p>
                      <p className="text-sm text-muted-foreground">Guests can use the kitchen</p>
                    </div>
                    <Switch
                      checked={formData.kitchen_included}
                      onCheckedChange={(v) => updateField('kitchen_included', v)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Staff Available</p>
                      <p className="text-sm text-muted-foreground">You can provide staff for events</p>
                    </div>
                    <Switch
                      checked={formData.staff_available}
                      onCheckedChange={(v) => updateField('staff_available', v)}
                    />
                  </div>
                  
                  {formData.staff_available && (
                    <div className="space-y-2 pl-4">
                      <Label>Describe staff services</Label>
                      <Input
                        value={formData.staff_description}
                        onChange={(e) => updateField('staff_description', e.target.value)}
                        placeholder="e.g., Bartenders, servers, security"
                        className="rounded-xl"
                      />
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Step 5: Rules & Policies */}
            {currentStep === 5 && (
              <>
                <div className="space-y-4">
                  <Label>Event Types Allowed *</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {EVENT_TYPES.map((type) => (
                      <div
                        key={type}
                        onClick={() => toggleArrayItem('event_types_allowed', type)}
                        className={`px-3 py-2 rounded-lg border cursor-pointer text-sm text-center transition-all ${
                          formData.event_types_allowed.includes(type)
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        {type}
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Minimum Age Requirement</Label>
                  <Select
                    value={formData.minimum_age}
                    onValueChange={(v) => updateField('minimum_age', v)}
                  >
                    <SelectTrigger className="rounded-xl w-1/2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_ages">All Ages</SelectItem>
                      <SelectItem value="18+">18+</SelectItem>
                      <SelectItem value="21+">21+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Smoking Allowed</span>
                      <Switch
                        checked={formData.smoking_allowed}
                        onCheckedChange={(v) => updateField('smoking_allowed', v)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Hookah Allowed</span>
                      <Switch
                        checked={formData.hookah_allowed}
                        onCheckedChange={(v) => updateField('hookah_allowed', v)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Has Cabaret License</span>
                      <Switch
                        checked={formData.has_cabaret_license}
                        onCheckedChange={(v) => updateField('has_cabaret_license', v)}
                      />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Outside Catering OK</span>
                      <Switch
                        checked={formData.outside_catering_allowed}
                        onCheckedChange={(v) => updateField('outside_catering_allowed', v)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">BYOB Allowed</span>
                      <Switch
                        checked={formData.byob_allowed}
                        onCheckedChange={(v) => updateField('byob_allowed', v)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Security Cameras</span>
                      <Switch
                        checked={formData.has_security_cameras}
                        onCheckedChange={(v) => updateField('has_security_cameras', v)}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Check-in Time</Label>
                    <Input
                      type="time"
                      value={formData.check_in_time}
                      onChange={(e) => updateField('check_in_time', e.target.value)}
                      className="rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Check-out Time</Label>
                    <Input
                      type="time"
                      value={formData.check_out_time}
                      onChange={(e) => updateField('check_out_time', e.target.value)}
                      className="rounded-xl"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>House Rules</Label>
                  <Textarea
                    value={formData.house_rules}
                    onChange={(e) => updateField('house_rules', e.target.value)}
                    placeholder="Any specific rules guests should know..."
                    className="rounded-xl"
                  />
                </div>
              </>
            )}

            {/* Step 6: Pricing */}
            {currentStep === 6 && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Hourly Rate ($)</Label>
                    <Input
                      type="number"
                      value={formData.hourly_rate}
                      onChange={(e) => updateField('hourly_rate', e.target.value)}
                      placeholder="150"
                      className="rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Daily Rate ($)</Label>
                    <Input
                      type="number"
                      value={formData.daily_rate}
                      onChange={(e) => updateField('daily_rate', e.target.value)}
                      placeholder="1000"
                      className="rounded-xl"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Minimum Hours</Label>
                  <Select
                    value={formData.minimum_hours.toString()}
                    onValueChange={(v) => updateField('minimum_hours', parseInt(v))}
                  >
                    <SelectTrigger className="rounded-xl w-1/2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 8].map((n) => (
                        <SelectItem key={n} value={n.toString()}>{n} hour{n > 1 ? 's' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Cleaning Fee ($)</Label>
                    <Input
                      type="number"
                      value={formData.cleaning_fee}
                      onChange={(e) => updateField('cleaning_fee', e.target.value)}
                      placeholder="100"
                      className="rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Security Deposit ($)</Label>
                    <Input
                      type="number"
                      value={formData.security_deposit}
                      onChange={(e) => updateField('security_deposit', e.target.value)}
                      placeholder="500"
                      className="rounded-xl"
                    />
                  </div>
                </div>
                
                <div className="space-y-4 pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Offer Discount</p>
                      <p className="text-sm text-muted-foreground">For longer bookings or special offers</p>
                    </div>
                    <Switch
                      checked={formData.offers_discount}
                      onCheckedChange={(v) => updateField('offers_discount', v)}
                    />
                  </div>
                  
                  {formData.offers_discount && (
                    <div className="grid grid-cols-2 gap-4 pl-4">
                      <div className="space-y-2">
                        <Label>Discount %</Label>
                        <Input
                          type="number"
                          value={formData.discount_percent}
                          onChange={(e) => updateField('discount_percent', e.target.value)}
                          placeholder="10"
                          className="rounded-xl"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Input
                          value={formData.discount_description}
                          onChange={(e) => updateField('discount_description', e.target.value)}
                          placeholder="e.g., 10% off 8+ hours"
                          className="rounded-xl"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Step 7: Photos */}
            {currentStep === 7 && (
              <>
                <div className="space-y-4">
                  <Label>Upload Photos of Your Venue *</Label>
                  <p className="text-sm text-muted-foreground">
                    Add at least 1 photo. High-quality images get more bookings.
                  </p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {photoUrls.map((url, idx) => (
                      <div key={idx} className="relative aspect-video rounded-xl overflow-hidden group">
                        <img src={url} alt={`Venue ${idx + 1}`} className="w-full h-full object-cover" />
                        <button
                          onClick={() => removePhoto(idx)}
                          className="absolute top-2 right-2 p-1 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    
                    <label className="aspect-video rounded-xl border-2 border-dashed border-border hover:border-primary/50 flex flex-col items-center justify-center cursor-pointer transition-colors">
                      <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                      <span className="text-sm text-muted-foreground">Add Photos</span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handlePhotoUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              </>
            )}

            {/* Step 8: Review */}
            {currentStep === 8 && (
              <div className="space-y-6">
                <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/30">
                  <div className="flex items-center gap-2 mb-2">
                    <Check className="h-5 w-5 text-green-500" />
                    <p className="font-semibold text-green-600">Ready to Publish!</p>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Review your venue details below. You can edit them anytime after publishing.
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Venue Name</span>
                    <span className="font-medium">{formData.name}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Type</span>
                    <span className="font-medium">{VENUE_TYPES.find(t => t.value === formData.venue_type)?.label}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Location</span>
                    <span className="font-medium">{formData.city}, {formData.state}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Capacity</span>
                    <span className="font-medium">{formData.capacity} guests</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Hourly Rate</span>
                    <span className="font-medium">${formData.hourly_rate}/hr</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Photos</span>
                    <span className="font-medium">{formData.photos.length} uploaded</span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => currentStep > 1 ? setCurrentStep(currentStep - 1) : navigate(-1)}
            className="rounded-xl"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            {currentStep > 1 ? 'Back' : 'Cancel'}
          </Button>
          
          {currentStep < STEPS.length ? (
            <Button
              onClick={() => setCurrentStep(currentStep + 1)}
              disabled={!canProceed()}
              className="rounded-xl"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="rounded-xl btn-glow"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Publishing...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Publish Venue
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ListVenue;
